import React, { useState } from 'react';
import { Button, Dialog, DialogActions, DialogContent, DialogTitle } from '@mui/material';
import { Check, FolderKanban, Plus } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useProjects } from '../hooks/useProjects';
import type { Project } from '../types';
import { ProjectFormDialog } from './ProjectFormDialog';

interface ProjectPickerDialogProps {
  open: boolean;
  /** Project the conversation currently belongs to, if any. */
  currentProjectId?: string | null;
  onSelect: (project: Project) => void | Promise<void>;
  onClose: () => void;
}

export const ProjectPickerDialog: React.FC<ProjectPickerDialogProps> = ({
  open,
  currentProjectId,
  onSelect,
  onClose,
}) => {
  const { t } = useTranslation();
  const { projects, canCreate, createProject } = useProjects();
  const [isFormOpen, setIsFormOpen] = useState(false);

  const handleSelect = async (project: Project) => {
    await onSelect(project);
    onClose();
  };

  return (
    <>
      <Dialog
        open={open && !isFormOpen}
        onClose={onClose}
        fullWidth
        maxWidth="xs"
        PaperProps={{ className: 'dark:bg-slate-800', sx: { borderRadius: '12px' } }}
      >
        <DialogTitle sx={{ fontWeight: 600 }} className="dark:text-slate-100">
          {t('projects.picker.title')}
        </DialogTitle>
        <DialogContent className="!px-3">
          {projects.length === 0 ? (
            <p className="px-3 py-6 text-center text-sm text-gray-500 dark:text-slate-400">
              {t('projects.picker.empty')}
            </p>
          ) : (
            <ul className="flex max-h-72 flex-col gap-1 overflow-y-auto">
              {projects.map((project) => {
                const isCurrent = project.publicId === currentProjectId;
                return (
                  <li key={project.publicId}>
                    <button
                      type="button"
                      disabled={isCurrent}
                      onClick={() => void handleSelect(project)}
                      className={`flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-left transition-colors ${
                        isCurrent
                          ? 'bg-blue-50 text-blue-700 dark:bg-blue-500/10 dark:text-blue-300'
                          : 'text-gray-800 hover:bg-gray-100 dark:text-slate-200 dark:hover:bg-slate-700'
                      }`}
                    >
                      <FolderKanban size={18} className="shrink-0" />
                      <span className="min-w-0 flex-1 truncate text-sm font-medium">
                        {project.name}
                      </span>
                      {isCurrent && <Check size={16} className="shrink-0" />}
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, justifyContent: 'space-between' }}>
          <Button
            size="small"
            startIcon={<Plus size={16} />}
            disabled={!canCreate}
            onClick={() => setIsFormOpen(true)}
          >
            {t('projects.picker.newProject')}
          </Button>
          <Button size="small" onClick={onClose}>
            {t('common.cancel')}
          </Button>
        </DialogActions>
      </Dialog>
      <ProjectFormDialog
        open={open && isFormOpen}
        onSubmit={createProject}
        onClose={() => setIsFormOpen(false)}
        onSuccess={(project) => void handleSelect(project)}
      />
    </>
  );
};
